import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormField, SelectionRowComponent, TagsInput, ZagButton } from '@zag/ui';
import { RolesManagementService } from '../roles-management.service';
import { UsersManagementService } from '../users-management.service';

export interface InviteUserDialogData {
  /** Optional: Pre-filled email address (e.g. when reconfiguring an existing invitation) */
  email?: string;
  /** Optional: Pre-selected role IDs */
  selectedRoleIds?: string[];
  /** Optional: Pre-filled message */
  message?: string;
}

export interface InviteUserDialogResult {
  success: true;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Predicate used both by the form validator and the TagsInput pill colouring */
function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}

function emailsArrayValidator(control: AbstractControl): ValidationErrors | null {
  const emails = control.value as string[] | null;
  if (!emails || emails.length === 0) {
    return { required: true };
  }
  const invalid = emails.filter(e => !isValidEmail(e));
  if (invalid.length > 0) {
    return { invalidEmails: invalid };
  }
  return null;
}

/**
 * Dialog for inviting new users.
 *
 * Collects one or more email addresses via the `zag-tags-input` pill input,
 * optional roles, and a personal message, then calls
 * `UsersManagementService.createInvitation()` once per address in parallel.
 * Returns `{ success: true }` when at least one invitation was sent so the
 * parent can show a toast and refresh the list.
 *
 * @example
 * ```typescript
 * const dialogRef = this.dialog.open(InviteUserDialogComponent, {
 *   width: 'min(96vw, 520px)',
 *   maxWidth: '96vw',
 *   maxHeight: '96dvh',
 *   panelClass: 'ai-chat-dialog-panel',
 *   backdropClass: 'ai-chat-dialog-backdrop',
 * });
 *
 * dialogRef.closed.subscribe((result) => {
 *   if (result?.success) {
 *     // At least one invitation was sent
 *   }
 * });
 * ```
 */
@Component({
  selector: 'sc-invite-user-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, FormField, TagsInput, SelectionRowComponent, ZagButton],
  templateUrl: './invite-user-dialog.component.html',
  styleUrl: './invite-user-dialog.component.scss',
})
export class InviteUserDialogComponent {
  private readonly dialogRef = inject(DialogRef<InviteUserDialogResult>);
  private readonly data = inject<InviteUserDialogData>(DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly rolesService = inject(RolesManagementService);
  private readonly usersService = inject(UsersManagementService);

  protected readonly form = this.fb.group({
    emails: [[] as string[], emailsArrayValidator],
    message: [''],
  });

  private readonly _selectedRoles = signal<Set<string>>(new Set());
  protected readonly selectedRoles = this._selectedRoles.asReadonly();

  protected readonly isSubmitting = signal(false);
  protected readonly submitError = signal<string | null>(null);

  protected readonly availableRoles = computed(() => this.rolesService.roles());

  /** Stable function reference passed to zag-tags-input for pill colouring */
  protected readonly isValidEmail = isValidEmail;

  constructor() {
    if (this.data?.selectedRoleIds) {
      this._selectedRoles.set(new Set(this.data.selectedRoleIds));
    }
    if (this.data?.email) {
      this.form.patchValue({ emails: [this.data.email] });
    }
    if (this.data?.message) {
      this.form.patchValue({ message: this.data.message });
    }
  }

  protected getEmailError(): string {
    const ctrl = this.form.controls.emails;
    if (!ctrl.touched || !ctrl.errors) return '';
    if (ctrl.errors['required']) return this.translate.instant('settings.users.invite.errorEmailsRequired');
    if (ctrl.errors['invalidEmails']) return this.translate.instant('settings.users.invite.errorEmailsInvalid');
    return '';
  }

  protected onRoleToggle(roleId: string, checked: boolean): void {
    const selected = new Set(this._selectedRoles());
    if (checked) {
      selected.add(roleId);
    } else {
      selected.delete(roleId);
    }
    this._selectedRoles.set(selected);
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onSubmit(): void {
    if (!this.form.valid || this.isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.submitError.set(null);

    const emails = this.form.controls.emails.value ?? [];
    const roleIds = Array.from(this._selectedRoles());
    const message = this.form.controls.message.value?.trim() || undefined;

    const requests = emails.map(email =>
      this.usersService.createInvitation({ email, roleIds, message }).pipe(
        map(() => true as const),
        catchError(() => of(false as const))
      )
    );

    forkJoin(requests).subscribe({
      next: results => {
        this.isSubmitting.set(false);
        const successes = results.filter(Boolean).length;
        if (successes > 0) {
          this.dialogRef.close({ success: true });
        } else {
          this.submitError.set('settings.users.invite.error');
        }
      },
      error: () => {
        this.isSubmitting.set(false);
        this.submitError.set('settings.users.invite.error');
      },
    });
  }
}
